/**
 * Clap Detection — Browser-based double-clap detector
 * Uses Web Audio API to monitor mic amplitude for sharp transients.
 * Double clap within 0.65s triggers callback.
 */
export class ClapDetector {
  constructor(onDoubleClap) {
    this.onDoubleClap = onDoubleClap;
    this.audioContext = null;
    this.analyser = null;
    this.stream = null;
    this.running = false;
    this.armed = true;
    
    // Detection params
    this.threshold = 0.45;        // Amplitude threshold for a clap
    this.doubleWindow = 650;      // ms between claps
    this.cooldown = 2000;         // ms after trigger before next detection
    this.firstClapTime = 0;
    this.lastTriggerTime = 0;
    
    this._rafId = null;
  }

  async start() {
    if (this.running) return true;
    
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: false, noiseSuppression: false } 
      });
      
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = 0.3;
      
      const source = this.audioContext.createMediaStreamSource(this.stream);
      source.connect(this.analyser);
      
      this.running = true;
      this._detect();
      
      console.log('[CLAP] Detection started');
      return true;
    } catch (e) {
      console.error('[CLAP] Failed to start:', e);
      return false;
    }
  }

  _detect() {
    if (!this.running || !this.analyser) return;
    
    const dataArray = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(dataArray);
    
    // Get peak amplitude
    let peak = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const abs = Math.abs(dataArray[i]);
      if (abs > peak) peak = abs;
    }
    
    const now = Date.now();
    
    // Check if in cooldown
    if (now - this.lastTriggerTime < this.cooldown) {
      this._rafId = requestAnimationFrame(() => this._detect());
      return;
    }
    
    if (this.armed && peak > this.threshold) {
      if (this.firstClapTime > 0 && (now - this.firstClapTime) < this.doubleWindow) {
        // DOUBLE CLAP DETECTED
        console.log('[CLAP] Double clap detected! Peak:', peak.toFixed(3));
        this.firstClapTime = 0;
        this.lastTriggerTime = now;
        
        if (this.onDoubleClap) {
          this.onDoubleClap();
        }
      } else {
        // First clap
        this.firstClapTime = now;
      }
    }
    
    // Reset first clap if window expired
    if (this.firstClapTime > 0 && (now - this.firstClapTime) > this.doubleWindow) {
      this.firstClapTime = 0;
    }
    
    this._rafId = requestAnimationFrame(() => this._detect());
  }

  stop() {
    this.running = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.analyser = null;
    console.log('[CLAP] Detection stopped');
  }

  setArmed(armed) {
    this.armed = armed;
  }
}
